/**
 * TenantSwitcher — dropdown showing every tenant the current identity has
 * access to. Selecting one calls `/api/auth/switch-tenant/{id}` which
 * re-issues a JWT with a `tenant_id` claim and then reloads the shell so
 * every subsequent request is scoped to the new workspace.
 *
 * Also exposes a "Nueva cuenta" action (iter 64) that creates an extra
 * independent tenant.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Building2, Check, ChevronDown } from "lucide-react";
import api, { formatApiError, setToken } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export default function TenantSwitcher() {
  const { lang } = useI18n();
  const [tenants, setTenants] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenants");
      setTenants(data.tenants || []);
      setActiveId(data.active_tenant_id);
    } catch (err) {
      console.error("tenants load failed:", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const switchTo = async (tenantId) => {
    if (tenantId === activeId) { setOpen(false); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/switch-tenant/${tenantId}`);
      if (data.access_token) setToken(data.access_token);
      // Full reload so every context (auth, i18n, queries) refetches under
      // the new tenant scope. Cleaner than trying to invalidate manually.
      window.location.reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const active = tenants.find((t) => t.tenant_id === activeId) || tenants[0];
  if (!active) return null;
  // Iter 64b — no self-service tenant creation. Extra workspaces only appear
  // here when the owner of another tenant invites this identity via
  // /team/invite-existing. If the identity only has their own tenant, hide
  // the dropdown chevron to avoid confusion.
  const hasMultiple = tenants.length > 1;

  return (
    <div className="relative" data-testid="tenant-switcher">
      <button
        onClick={() => hasMultiple && setOpen((o) => !o)}
        disabled={!hasMultiple}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left border border-transparent transition-colors ${hasMultiple ? "hover:bg-[var(--surface-hover)] hover:border-[var(--border-color)]" : "cursor-default"}`}
        data-testid="tenant-switcher-button"
      >
        {active.company_logo_url ? (
          <img
            src={active.company_logo_url}
            alt=""
            className="w-7 h-7 rounded object-contain bg-white p-0.5 border border-[var(--border-color)]"
          />
        ) : (
          <div className="w-7 h-7 rounded bg-[var(--brand-accent)]/15 grid place-items-center text-[var(--brand-accent)]">
            <Building2 size={14} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] leading-tight">
            {lang === "es" ? "Cuenta activa" : "Active workspace"}
          </div>
          <div className="text-sm font-medium truncate" data-testid="tenant-switcher-name">
            {active.name}
          </div>
        </div>
        {hasMultiple && (
          <ChevronDown size={14} strokeWidth={2} className="text-[var(--text-muted)]" />
        )}
      </button>

      {open && hasMultiple && (
        <div
          className="absolute left-2 right-2 top-full mt-1 rounded-md border border-[var(--border-color)] bg-[var(--surface)] shadow-lg py-1 z-40 max-h-80 overflow-y-auto"
          data-testid="tenant-switcher-list"
        >
          {tenants.map((t) => (
            <button
              key={t.tenant_id}
              onClick={() => switchTo(t.tenant_id)}
              disabled={busy}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--surface-hover)] text-left ${t.tenant_id === activeId ? "bg-[var(--surface-hover)]" : ""}`}
              data-testid={`tenant-option-${t.tenant_id}`}
            >
              {t.company_logo_url ? (
                <img src={t.company_logo_url} alt="" className="w-6 h-6 rounded object-contain bg-white p-0.5" />
              ) : (
                <div className="w-6 h-6 rounded bg-[var(--brand-accent)]/15 grid place-items-center text-[var(--brand-accent)]">
                  <Building2 size={12} />
                </div>
              )}
              <span className="flex-1 truncate">{t.name}</span>
              {t.role !== "owner" && (
                <span className="text-[10px] uppercase text-[var(--text-muted)] tracking-wider">
                  {t.role}
                </span>
              )}
              {t.tenant_id === activeId && <Check size={13} className="text-[var(--brand-accent)]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
